import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { INPUT_LIGHT } from "@/lib/constants";
import { MaskLine } from "@/components/site/Reveal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function Auth({ mode }) {
  const navigate = useNavigate();
  const { user, login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [regForm, setRegForm] = useState({ name: "", email: "", password: "", role: "candidate", company: "" });

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const submitLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(loginForm.email, loginForm.password);
      toast.success("Welcome back.");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await register({
        name: regForm.name,
        email: regForm.email,
        password: regForm.password,
        role: regForm.role,
        company: regForm.role === "employer" ? regForm.company || null : null,
      });
      toast.success("Account created. Welcome to Samanvay HR.");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = INPUT_LIGHT;

  return (
    <main data-testid="auth-page" className="min-h-screen pt-40 pb-32 flex justify-center px-6">
      <div className="w-full max-w-md">
        <h1 className="font-display font-semibold tracking-tighter text-4xl sm:text-5xl text-slate-900 text-center">
          <MaskLine delay={0.1}>{mode === "login" ? "Welcome back to Samanvay." : "Join Samanvay HR."}</MaskLine>
        </h1>
        <p className="mt-4 text-center text-sm text-slate-500">
          {mode === "login" ? "Sign in to your portal." : "Create your candidate or employer account."}
        </p>

        <div className="mt-10 rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
          <Tabs value={mode} onValueChange={(v) => navigate(v === "login" ? "/login" : "/register")}>
            <TabsList className="grid w-full grid-cols-2 bg-slate-100" data-testid="auth-tabs">
              <TabsTrigger value="login" data-testid="auth-tab-login" className="text-slate-500 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm">Sign in</TabsTrigger>
              <TabsTrigger value="register" data-testid="auth-tab-register" className="text-slate-500 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm">Register</TabsTrigger>
            </TabsList>
          </Tabs>

          {error && (
            <p className="mt-6 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-600" data-testid="auth-error">
              {error}
            </p>
          )}

          <button
            type="button"
            onClick={googleSignIn}
            data-testid="google-signin-btn"
            className="mt-6 flex w-full items-center justify-center gap-3 rounded-full border border-slate-200 bg-white py-3.5 text-sm font-medium text-slate-700 transition-[border-color,box-shadow] duration-300 hover:border-slate-300 hover:shadow-glow-sm"
          >
            <svg width="17" height="17" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z" />
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
              <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
              <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C36.9 39.2 44 34 44 24c0-1.3-.1-2.6-.4-3.9z" />
            </svg>
            Continue with Google
          </button>

          <div className="mt-6 flex items-center gap-4">
            <span className="h-px flex-1 bg-slate-200" />
            <span className="text-[11px] uppercase tracking-[0.2em] text-slate-400">or with email</span>
            <span className="h-px flex-1 bg-slate-200" />
          </div>

          {mode === "login" ? (
            <form onSubmit={submitLogin} className="mt-6 space-y-5" data-testid="login-form">
              <div className="space-y-2">
                <Label htmlFor="login-email" className="text-slate-500">Email</Label>
                <Input id="login-email" type="email" required value={loginForm.email} data-testid="login-email-input"
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} className={inputCls} placeholder="you@email.com" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="login-password" className="text-slate-500">Password</Label>
                <Input id="login-password" type="password" required value={loginForm.password} data-testid="login-password-input"
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} className={inputCls} placeholder="••••••••" />
              </div>
              <button type="submit" disabled={loading} data-testid="login-submit-btn"
                className="w-full rounded-full bg-brand py-3.5 text-sm font-medium text-white transition-all duration-300 hover:bg-[#E63E12] hover:shadow-glow disabled:opacity-50">
                {loading ? "Signing in..." : "Sign in"}
              </button>
            </form>
          ) : (
            <form onSubmit={submitRegister} className="mt-6 space-y-5" data-testid="register-form">
              <div className="grid grid-cols-2 gap-3" data-testid="register-role-picker">
                {[
                  { v: "candidate", label: "I want a job" },
                  { v: "employer", label: "I'm hiring" },
                ].map((r) => (
                  <button
                    key={r.v}
                    type="button"
                    onClick={() => setRegForm({ ...regForm, role: r.v })}
                    data-testid={`register-role-${r.v}`}
                    className={`rounded-full border py-3 text-sm transition-colors duration-200 ${
                      regForm.role === r.v
                        ? "border-brand bg-orange-50 text-brand font-medium"
                        : "border-slate-200 bg-white text-slate-500 hover:text-slate-900"
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
              <div className="space-y-2">
                <Label htmlFor="reg-name" className="text-slate-500">Full name</Label>
                <Input id="reg-name" required value={regForm.name} data-testid="register-name-input"
                  onChange={(e) => setRegForm({ ...regForm, name: e.target.value })} className={inputCls} placeholder="Asha Verma" />
              </div>
              {regForm.role === "employer" && (
                <div className="space-y-2">
                  <Label htmlFor="reg-company" className="text-slate-500">Company</Label>
                  <Input id="reg-company" required value={regForm.company} data-testid="register-company-input"
                    onChange={(e) => setRegForm({ ...regForm, company: e.target.value })} className={inputCls} placeholder="Company Pvt. Ltd." />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="reg-email" className="text-slate-500">Email</Label>
                <Input id="reg-email" type="email" required value={regForm.email} data-testid="register-email-input"
                  onChange={(e) => setRegForm({ ...regForm, email: e.target.value })} className={inputCls} placeholder="you@email.com" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reg-password" className="text-slate-500">Password (min 8 characters)</Label>
                <Input id="reg-password" type="password" required minLength={8} value={regForm.password} data-testid="register-password-input"
                  onChange={(e) => setRegForm({ ...regForm, password: e.target.value })} className={inputCls} placeholder="••••••••" />
              </div>
              <button type="submit" disabled={loading} data-testid="register-submit-btn"
                className="w-full rounded-full bg-brand py-3.5 text-sm font-medium text-white transition-all duration-300 hover:bg-[#E63E12] hover:shadow-glow disabled:opacity-50">
                {loading ? "Creating account..." : `Create ${regForm.role} account`}
              </button>
            </form>
          )}
        </div>
      </div>
    </main>
  );
}
